import {
  Enums,
  cache,
  ProgressiveRetrieveImages,
  utilities,
  RenderingEngine,
  metaData,
  type Types,
  setUseCPURendering,
} from '@cornerstonejs/core';
import {
  initDemo,
  createImageIdsAndCacheMetaData,
  setTitleAndDescription,
  getLocalUrl,
} from '../../../../utils/demo/helpers';

const { imageRetrieveMetadataProvider } = utilities;
const { sequentialRetrieveStages } = ProgressiveRetrieveImages;
const { Events } = Enums;
// This is for debugging purposes
console.warn(
  'Click on index.ts to open source code for this example --------->'
);

const { ViewportType, ImageQualityStatus } = Enums;

// ======== Set up page ======== //
setTitleAndDescription(
  'Progressive Stack',
  'Displays a single DICOM image in a Stack viewport after clicking the load button.'
);

const content = document.getElementById('content');

const instructions = document.createElement('p');
instructions.innerText = 'Click on a button to perform the given load type';
content.appendChild(instructions);

const loaders = document.createElement('div');
content.appendChild(loaders);

const timingInfo = document.createElement('div');
timingInfo.style.width = '45em';
timingInfo.style.height = '10em';
timingInfo.style.float = 'left';
timingInfo.innerText = 'Timing Info Here';
content.appendChild(timingInfo);

const itemInfo = document.createElement('div');
itemInfo.style.width = '25em';
itemInfo.style.height = '10em';
itemInfo.style.float = 'left';
content.appendChild(itemInfo);
itemInfo.innerHTML = `
<ul>
<li>JLS Thumbnail - small JLS thumbnails only</li>
<li>JLS Mixed - thumbnail first, then full</li>
<li>HTJ2K - streaming load, first decode at the default 128k</li>
<li>HTJ2K 32k First - the same load, first decode at 32k</li>
<li>HTJ2K - lossy byte range then lossy full</li>
<li>Bytes - full resolution 64k bytes, then full final</li>
</ul>
`;

const devicePixelRatio = window.devicePixelRatio || 1;
const element = document.createElement('div');
element.id = 'cornerstone-element';
// Use devicePixelRatio here so that the window size fits all pixels, but not
// larger than that.
element.style.width = `${3036 / devicePixelRatio}px`;
element.style.height = `${3036 / devicePixelRatio}px`;
element.style.clear = 'both';
content.appendChild(element);

// ============================= //

const statusNames = {
  [ImageQualityStatus.FULL_RESOLUTION]: 'full resolution',
  [ImageQualityStatus.LOSSY]: 'lossy',
  [ImageQualityStatus.SUBRESOLUTION]: 'sub-resolution',
};

let startTime = Date.now();

async function newImageFunction(evt) {
  const { image } = evt.detail;
  const {
    imageQualityStatus: status,
    decodeTimeInMS,
    loadTimeInMS,
    transferSyntaxUID,
  } = image;
  const complete = status === ImageQualityStatus.FULL_RESOLUTION;
  if (complete) {
    element.removeEventListener(Events.STACK_NEW_IMAGE, newImageFunction);
  }
  const completeText = statusNames[status] || `other ${status}`;
  const totalTime = Date.now() - startTime;
  timingInfo.innerHTML += `<p style="margin:0">Render ${completeText} of ${transferSyntaxUID} took ${loadTimeInMS} ms to load and ${decodeTimeInMS} to decode ${totalTime} total</p>`;
}

async function showStack(
  stack: string[],
  viewport,
  retrieveConfiguration,
  name: string
) {
  cache.purgeCache();
  imageRetrieveMetadataProvider.clear();
  if (retrieveConfiguration) {
    imageRetrieveMetadataProvider.add('stack', retrieveConfiguration);
  }
  timingInfo.innerHTML = `<p id="loading" style="margin:0">Loading ${name}</p>`;
  startTime = Date.now();
  element.addEventListener(Events.STACK_NEW_IMAGE, newImageFunction);
  const start = Date.now();
  // Set the stack on the viewport
  await viewport.setStack(stack, 0, retrieveConfiguration);

  // Render the image
  viewport.render();
  const end = Date.now();
  const { transferSyntaxUID } = metaData.get('transferSyntax', stack[0]);
  document.getElementById('loading').innerText = `Stack render took ${
    end - start
  } using ${transferSyntaxUID}`;
}

/**
 * The alternate renditions these configurations retrieve are generated by
 * static DICOMweb's `createdicomweb` CLI, which replaces the old
 * `mkdicomweb create --alternate ...` invocations. `create` writes the base
 * store (primary `frames/` keep the source transfer syntax) and `alternates`
 * writes the rendition directories beside it. Asking for all four renditions
 * in one `alternates` pass decodes each frame once rather than four times.
 *
 * Both studies used below need the same treatment, since the byte range and
 * streaming configurations retrieve from `htj2k/` for either one:
 *
 * ```bash
 * cd /src/static-dicomweb
 * createdicomweb create "/dicom/DE Images for Rad"
 * createdicomweb alternates 1.3.6.1.4.1.9590.100.1.2.19841440611855834937505752510708699165 \
 *   --jls --jls-thumbnail --htj2k --htj2k-lossy
 *
 * createdicomweb create /src/viewer-testdata/dcm/Juno
 * createdicomweb alternates 1.3.6.1.4.1.25403.345050719074.3824.20170125113417.1 \
 *   --series-uid 1.3.6.1.4.1.25403.345050719074.3824.20170125113545.4 \
 *   --jls --jls-thumbnail --htj2k --htj2k-lossy
 * ```
 *
 * The rendition directory names below are the `alternates` names: `jls` (full
 * resolution JPEG-LS), `jlsThumbnail` (quarter resolution JPEG-LS), `htj2k`
 * (full resolution lossless HTJ2K) and `htj2kLossy` (full resolution lossy
 * HTJ2K, previously called `lossy`). Serve the result with `dicomwebserver`
 * and load the example with `?useLocal=true`.
 */
const jlsRetrieveOptions = {
  retrieveOptions: {
    default: {
      framesPath: '/jls/',
    },
  },
};

const jlsThumbnailOptions = {
  retrieveOptions: {
    default: {
      framesPath: '/jlsThumbnail/',
    },
  },
};

const jlsMixedOptions = {
  ...sequentialRetrieveStages,
  retrieveOptions: {
    singleFast: {
      imageQualityStatus: ImageQualityStatus.SUBRESOLUTION,
      framesPath: '/jlsThumbnail/',
    },
    singleFinal: {
      framesPath: '/jls/',
    },
  },
};

// Primary frames/ keep the source transfer syntax, which for these studies is
// not HTJ2K, so the HTJ2K configurations retrieve from the htj2k/ rendition
// instead. A retrieve type with no entry of its own falls back to `default`,
// so setting `default` is enough to move a whole configuration.
const htj2kFrames = { framesPath: '/htj2k/' };

const htj2kProgressiveOptions = {
  retrieveOptions: {
    default: htj2kFrames,
    single: {
      ...htj2kFrames,
      streaming: true,
      decodeLevel: 0,
    },
  },
};

/**
 * The same streaming retrieve, with the initial size the default used before it
 * became 128k.
 *
 * The two configurations together show what that size buys on one large frame.
 * 32k puts an image on screen sooner, because a quarter of the data has to
 * arrive first. The image is poorer, because 32k of codestream covers the whole
 * 3036x3036 frame, and the next decode follows almost at once and replaces it.
 * Compare the time and the quality of the first render of each.
 */
const htj2kInitial32kOptions = {
  retrieveOptions: {
    default: htj2kFrames,
    single: {
      ...htj2kFrames,
      streaming: true,
      decodeLevel: 0,
      initialChunkSize: 32 * 1024,
    },
  },
};

const htj2kLossyOptions = {
  ...sequentialRetrieveStages,
  retrieveOptions: {
    default: htj2kFrames,
    singleFast: {
      imageQualityStatus: ImageQualityStatus.LOSSY,
      framesPath: '/htj2kLossy/',
      streaming: true,
    },
  },
};

// Every stage here decodes at full resolution (decodeLevel 0), including the
// first one that has only 32k of codestream to work with.  OpenJPH decodes the
// truncated remainder instead of throwing, so no sub-resolution fallback ladder
// is needed, and a full resolution decode of 32k is visibly better than
// decoding a quarter-size image and scaling it back up.
const htj2kMixedOptions = {
  stages: [
    {
      id: 'lossySequential',
      retrieveType: 'singleFast',
    },
    {
      id: 'lossyMiddle',
      retrieveType: 'singleMiddle',
    },
    {
      id: 'finalSequential',
      retrieveType: 'singleFinal',
    },
  ],
  retrieveOptions: {
    default: htj2kFrames,
    singleFast: {
      ...htj2kFrames,
      decodeLevel: 0,
      rangeIndex: 0,
    },
    // Note how the rangeIndex increases significantly to get much more data
    singleMiddle: {
      ...htj2kFrames,
      decodeLevel: 0,
      rangeIndex: 10,
    },
    singleFinal: {
      ...htj2kFrames,
      // Just do the final rangeIndex retrieve
      rangeIndex: -1,
    },
  },
};

/**
 * Runs the demo
 */
async function run() {
  // Init Cornerstone and related libraries
  await initDemo();

  // Get Cornerstone imageIds and fetch metadata into RAM
  const imageIds = await createImageIdsAndCacheMetaData({
    StudyInstanceUID:
      '1.3.6.1.4.1.9590.100.1.2.19841440611855834937505752510708699165',
    SeriesInstanceUID:
      '1.3.6.1.4.1.9590.100.1.2.160160590111755920740089886004263812825',
    // This study is not on the public store; run with ?useLocal=true against a
    // local static DICOMweb server to load it.
    wadoRsRoot:
      getLocalUrl() || 'https://d14fa38qiwhyfd.cloudfront.net/dicomweb',
  });

  const imageIdsCt = await createImageIdsAndCacheMetaData({
    StudyInstanceUID: '1.3.6.1.4.1.25403.345050719074.3824.20170125113417.1',
    SeriesInstanceUID: '1.3.6.1.4.1.25403.345050719074.3824.20170125113545.4',
    wadoRsRoot:
      getLocalUrl() || 'https://d14fa38qiwhyfd.cloudfront.net/dicomweb',
  });

  // Instantiate a rendering engine
  const renderingEngineId = 'myRenderingEngine';
  const renderingEngine = new RenderingEngine(renderingEngineId);

  // Create a stack viewport
  const viewportId = 'stackViewport';
  const viewportInput = {
    viewportId,
    type: ViewportType.STACK,
    element,
    defaultOptions: {
      background: [0.2, 0, 0.2] as Types.Point3,
    },
  };

  renderingEngine.enableElement(viewportInput);

  // Get the stack viewport that was created
  const viewport = renderingEngine.getViewport(
    viewportId
  ) as Types.IStackViewport;

  const createButton = (text, action) => {
    const button = document.createElement('button');
    button.innerText = text;
    button.id = text;
    button.onclick = action;
    loaders.appendChild(button);
    return button;
  };

  const loadButton = (text, imageIds, retrieveConfiguration) => {
    return createButton(
      text,
      showStack.bind(null, imageIds, viewport, retrieveConfiguration, text)
    );
  };

  loadButton('JLS', imageIds, jlsRetrieveOptions);

  loadButton('JLS Thumbnail', imageIds, jlsThumbnailOptions);
  loadButton('JLS Mixed', imageIds, jlsMixedOptions);

  loadButton('HTJ2K Non Progressive', imageIds, undefined);
  loadButton('HTJ2K', imageIds, htj2kProgressiveOptions);
  loadButton('HTJ2K 32k First', imageIds, htj2kInitial32kOptions);
  loadButton('HTJ2K Lossy', imageIds, htj2kLossyOptions);
  loadButton('HTJ2K Bytes', imageIds, htj2kMixedOptions);

  loadButton('CT JLS Mixed', imageIdsCt, jlsMixedOptions);
  loadButton('CT HTJ2K Bytes', imageIdsCt, htj2kMixedOptions);

  createButton('Set CPU', (onclick) => {
    const button = document.getElementById('Set CPU');
    const cpuValue = button.innerText === 'Set CPU';
    setUseCPURendering(cpuValue);
    viewport.setUseCPURendering(cpuValue);
    button.innerText = cpuValue ? 'Set GPU' : 'Set CPU';
  });
}

run();
